import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Pagination,
  Spinner,
  Alert,
  Card,
  Badge,
} from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash, FaUpload } from "react-icons/fa";

const API_URL = "https://radheyelectrical.com/api/api_add_client.php";
const BASE_IMAGE_URL = "https://radheyelectrical.com/api/";

export default function ClientAdmin() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const clientsPerPage = 20;

  const [formData, setFormData] = useState({
    id: "",
    name: "",
    description: "",
    logo: null,
    existing_logo: "",
    preview: "",
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const res = await fetch(API_URL);
      const data = await res.json();

      if (data.status === "success") {
        setClients(data.data);
      }
    } catch {
      setError("Failed to load clients.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      id: "",
      name: "",
      description: "",
      logo: null,
      existing_logo: "",
      preview: "",
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this client?")) return;

    try {
      const res = await fetch(`${API_URL}?id=${id}`, {
        method: "DELETE",
      });
      const data = await res.json();

      if (data.status === "success") fetchClients();
      else alert(data.message);
    } catch (err) {
      console.error(err);
    }
  };

  // ✅ FIX URL
  const fixImageUrl = (url) => {
    if (!url) return null;

    if (url.includes("/api/")) return url;

    return url.replace(
      "https://radheyelectrical.com/",
      "https://radheyelectrical.com/api/"
    );
  };

  const handleEdit = (client) => {
    const fixedUrl = fixImageUrl(client.logo_url);

    setFormData({
      id: client.id,
      name: client.name,
      description: client.description || "",
      logo: null,
      existing_logo: fixedUrl,
      preview: fixedUrl,
    });

    setShowModal(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowed = ["image/png", "image/jpeg", "image/jpg"];
    if (!allowed.includes(file.type)) {
      alert("Only PNG, JPG, JPEG allowed");
      return;
    }

    setFormData({
      ...formData,
      logo: file,
      preview: URL.createObjectURL(file),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = new FormData();
    data.append("id", formData.id);
    data.append("name", formData.name);
    data.append("description", formData.description);

    if (formData.existing_logo) {
      const fileName = formData.existing_logo.split("/").pop();
      data.append("existing_logo", `client/${fileName}`);
    }

    if (formData.logo) {
      data.append("logo", formData.logo);
    }

    try {
      const res = await fetch(API_URL, {
        method: "POST",
        body: data,
      });

      const result = await res.json();

      if (result.status === "success") {
        setShowModal(false);
        resetForm();
        fetchClients();
      } else {
        alert(result.message);
      }
    } catch {
      alert("Upload failed");
    }
  };

  const indexLast = currentPage * clientsPerPage;
  const indexFirst = indexLast - clientsPerPage;
  const currentClients = clients.slice(indexFirst, indexLast);
  const totalPages = Math.ceil(clients.length / clientsPerPage);

  return (
    <Card className="shadow-sm border-0">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="fw-bold text-primary mb-0">Manage Clients</h5>
        <Button
          variant="success"
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
        >
          <FaPlus className="me-2" /> Add Client
        </Button>
      </Card.Header>

      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Logo</th>
                  <th>Name</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>

              <tbody>
                {currentClients.map((client) => {
                  const imageUrl =
                    fixImageUrl(client.logo_url) ||
                    BASE_IMAGE_URL + "client/default.png";

                  return (
                    <tr key={client.id}>
                      <td>
                        <img
                          src={imageUrl}
                          alt="logo"
                          style={{
                            height: 40,
                            width: 80,
                            objectFit: "contain",
                          }}
                          className="border rounded p-1 bg-light"
                          onError={(e) => {
                            e.target.onerror = null; // ✅ STOP LOOP
                            e.target.src =
                              BASE_IMAGE_URL + "client/default.png";
                          }}
                        />
                      </td>

                      <td>{client.name}</td>

                      <td className="text-end">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleEdit(client)}
                        >
                          <FaEdit />
                        </Button>{" "}
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDelete(client.id)}
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            {totalPages > 1 && (
              <Pagination className="justify-content-center">
                {[...Array(totalPages)].map((_, i) => (
                  <Pagination.Item
                    key={i}
                    active={i + 1 === currentPage}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </Pagination.Item>
                ))}
              </Pagination>
            )}
          </>
        )}
      </Card.Body>

      {/* MODAL */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {formData.id ? "Edit Client" : "Add Client"}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Client Name</Form.Label>
              <Form.Control
                required
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Logo</Form.Label>

              {formData.preview && (
                <div className="mb-2">
                  <Badge bg="light" text="dark">
                    Preview
                  </Badge>
                  <img
                    src={formData.preview}
                    alt="preview"
                    height="60"
                    className="d-block border rounded mt-1"
                    onError={(e) => (e.target.style.display = "none")}
                  />
                </div>
              )}

              <Form.Control
                type="file"
                accept=".png,.jpg,.jpeg"
                onChange={handleFileChange}
              />
            </Form.Group>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button type="submit">
              <FaUpload className="me-2" />
              {formData.id ? "Update" : "Upload"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Card>
  );
}
